/*
  T5 compare page. Picks live in the address (?robots=handle,handle; ?models=sora-70,aquasense-2-pro also works),
  so links from the collection compare tray and product pages open pre-filled.
  No picks: every robot on desktop, the default picks on phones (max 3 on phones, min 2 everywhere).
  Highlight differences (T5 DiffsD): rows that differ across the robots in view get a grey band, rows that are the
  same fade back. Compared on what each cell shows (its text, ticks included), re-checked whenever picks change.
  Phones and tablets: once the robot cards stick to the top, they shrink to picture, name and price (is-condensed),
  so more of the comparison shows; back at the top of the table they're full size again.
*/
class ComparePage extends HTMLElement {
  connectedCallback() {
    this.cols = [...this.querySelectorAll('thead th[data-col]')];
    this.picks = [...this.querySelectorAll('[data-pick]')];
    this.rows = [...this.querySelectorAll('[data-row]')];
    this.count = this.querySelector('[data-count]');
    this.table = this.querySelector('.cpg-table');
    this.diff = this.querySelector('[data-diff]');
    this.phone = window.matchMedia('(max-width: 749px)');
    this.maxPhone = parseInt(this.dataset.maxMobile, 10) || 3;

    this.chosen = this.fromAddress();
    if (!this.chosen.length) this.chosen = this.fallback();
    this.clamp();
    this.apply(false);

    this.picks.forEach((box) => box.addEventListener('change', () => this.toggle(box)));
    this.diff.addEventListener('change', () => this.highlight());
    this.phone.addEventListener('change', () => { this.clamp(); this.apply(false); });

    this.stickTop();
    window.addEventListener('resize', () => this.stickTop(), { passive: true });

    this.small = window.matchMedia('(max-width: 989px)');
    // One position check per scroll event (no animation-frame gate: a background tab never runs it, and the gate stuck)
    window.addEventListener('scroll', () => this.condense(), { passive: true });
    this.small.addEventListener('change', () => this.condense());
    this.condense();
  }

  // The cards are stuck once the table's top has scrolled past the sticky line
  condense() {
    const top = parseFloat(this.style.getPropertyValue('--cpg-top')) || 0;
    const stuck = this.small.matches && this.table.getBoundingClientRect().top < top - 8;
    if (stuck !== this.table.classList.contains('is-condensed')) this.table.classList.toggle('is-condensed', stuck);
  }

  resolve(name) {
    const want = name.trim().toLowerCase();
    if (!want) return null;
    const col = this.cols.find((c) => c.dataset.alias.split(' ').includes(want));
    return col ? col.dataset.col : null;
  }

  fromAddress() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('robots') || params.get('models') || '';
    const out = [];
    raw.split(',').forEach((name) => {
      const handle = this.resolve(decodeURIComponent(name));
      if (handle && !out.includes(handle)) out.push(handle);
    });
    return out;
  }

  fallback() {
    const all = this.cols.map((c) => c.dataset.col);
    if (!this.phone.matches) return all;
    const defaults = (this.dataset.defaults || '').split(',').filter((h) => all.includes(h));
    return defaults.length >= 2 ? defaults : all.slice(0, 2);
  }

  clamp() {
    if (this.phone.matches && this.chosen.length > this.maxPhone) this.chosen = this.chosen.slice(0, this.maxPhone);
  }

  toggle(box) {
    const handle = box.value;
    if (box.checked) {
      if (this.phone.matches && this.chosen.length >= this.maxPhone) {
        box.checked = false;
        return this.say(this.dataset.msgMax);
      }
      this.chosen.push(handle);
      // Keep the table in the collection's order
      const order = this.cols.map((c) => c.dataset.col);
      this.chosen.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    } else {
      if (this.chosen.length <= 2) {
        box.checked = true;
        return this.say(this.dataset.msgMin);
      }
      this.chosen = this.chosen.filter((h) => h !== handle);
    }
    this.apply(true);
  }

  apply(remember) {
    const on = new Set(this.chosen);
    this.querySelectorAll('[data-col]').forEach((cell) => { cell.hidden = !on.has(cell.dataset.col); });
    this.picks.forEach((box) => { box.checked = on.has(box.value); });
    this.table.style.setProperty('--shown', String(on.size));
    this.querySelectorAll('.cpg-group th').forEach((th) => { th.colSpan = on.size + 1; });

    const label = (this.count.dataset.label || '').replace('[n]', on.size);
    this.count.textContent = on.size > 3 ? `${label}. ${this.count.dataset.hint || ''}` : label;

    if (remember) {
      const url = new URL(window.location.href);
      url.searchParams.delete('models');
      url.searchParams.set('robots', this.chosen.join(','));
      window.history.replaceState(null, '', url.toString().replace(/%2C/g, ','));
    }
    this.highlight();
  }

  highlight() {
    this.querySelector('[data-table]').dataset.highlight = this.diff.checked ? 'on' : 'off';
    this.rows.forEach((row) => {
      const values = [...row.querySelectorAll('td')].filter((td) => !td.hidden).map((td) => td.textContent.replace(/\s+/g, ' ').trim());
      const same = values.every((v) => v === values[0]);
      row.classList.toggle('is-same', same);
      row.classList.toggle('is-diff', !same);
    });
  }

  say(text) {
    if (text) this.count.textContent = text;
  }

  stickTop() {
    const header = document.querySelector('sticky-header');
    const height = header ? Math.round(header.getBoundingClientRect().height) : 0;
    this.style.setProperty('--cpg-top', `${height}px`);
  }
}

if (!customElements.get('compare-page')) customElements.define('compare-page', ComparePage);
